import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Battery, Check, X, Sun, Home as HomeIcon, Send, Coins, Moon, ArrowDownCircle, ShieldCheck, AlertTriangle, TrendingUp } from "lucide-react";
import { useCalculatorResult } from "../context/CalculatorResultContext";

/* ------- helpers ------- */
const scrollToCalc = (e) => {
  if (e) e.preventDefault();
  const el = document.getElementById("calculator");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* ------- compact row ------- */
const Row = ({ icon: Icon, text, tone = "neutral" }) => {
  const toneCls =
    tone === "pos" ? "bg-emerald-500/10 text-emerald-300 border-emerald-300/20" :
    tone === "neg" ? "bg-rose-500/10 text-rose-300 border-rose-300/20" :
    "bg-white/10 text-slate-200 border-white/10";
  return (
    <li className="flex items-center gap-2.5">
      <span className={`w-7 h-7 rounded-lg border ${toneCls} flex items-center justify-center flex-shrink-0`}>
        <Icon size={13} />
      </span>
      <span className="text-slate-200 text-[13.5px] leading-snug">{text}</span>
    </li>
  );
};

const SalderingsSection = () => {
  const { payload, result } = useCalculatorResult();

  // Derive personalised numbers from calculator state (no recompute, just shape)
  const monthlyCost = payload?.monthly_cost ?? 180;
  const annualCostWithout = Math.round(monthlyCost * 12); // baseline = current bill
  const annualSavings = result ? Number(result.estimated_annual_savings) : 0;
  const annualCostWith = Math.max(0, Math.round(annualCostWithout - annualSavings));
  const selfConsWith = result ? Number(result.self_consumption_pct) : 90;
  const selfConsWithout = 30;
  const hasResult = !!result;

  const fmtEuro = (n) => `€ ${Number(n).toLocaleString("nl-NL")}`;

  return (
    <section id="saldering" className="py-14 lg:py-16 relative overflow-hidden bg-navy-900 text-white" data-testid="saldering-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="blob-pink absolute -top-24 -left-24 opacity-40" />
        <div className="blob-pink absolute bottom-0 right-0 opacity-25" />
        <div className="absolute inset-0 noise-bg opacity-25" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        {/* Compact header */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-brand mb-2.5">Wijziging 2027</p>
          <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-balance leading-tight">
            Wat verandert er na het afschaffen van de salderingsregeling?
          </h2>
          <p className="mt-3 text-slate-300 text-[15px] leading-relaxed">
            Vanaf 2027 ontvang je nog maar een beperkte vergoeding voor teruggeleverde stroom. Met een thuisbatterij gebruik je veel meer van je eigen opgewekte energie.
          </p>

          <div className="mt-5 flex flex-col items-center gap-3">
            <a
              href="#calculator"
              onClick={scrollToCalc}
              className="btn-pink !text-white !py-2.5 !px-5 !text-sm"
              data-testid="saldering-cta-top"
            >
              Bereken mijn besparing <ArrowRight size={16} />
            </a>
            <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-slate-400">
              {["Binnen 30 seconden inzicht", "Geen contactgegevens nodig", "Direct resultaat"].map((b) => (
                <li key={b} className="flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full bg-brand/20 text-brand flex items-center justify-center">
                    <Check size={10} strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Compact comparison cards */}
        <div className="mt-10 grid md:grid-cols-2 gap-4 lg:gap-5">
          {/* WITHOUT */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 hover:-translate-y-1 transition-all duration-500"
            data-testid="saldering-card-without"
          >
            <div className="absolute -inset-px rounded-3xl pointer-events-none bg-gradient-to-br from-rose-500/10 via-transparent to-transparent" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-9 h-9 rounded-xl bg-rose-500/15 text-rose-300 border border-rose-400/20 flex items-center justify-center flex-shrink-0">
                    <X size={16} strokeWidth={2.5} />
                  </span>
                  <h3 className="font-heading text-lg font-black">Zonder thuisbatterij</h3>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold leading-none">Energiekosten / jaar</div>
                  <div className="font-heading font-black text-2xl tabular-nums leading-none mt-1" data-testid="sald-cost-without">
                    {fmtEuro(annualCostWithout)}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Eigen verbruik {selfConsWithout}%</div>
                </div>
              </div>

              <ul className="space-y-2.5">
                <Row icon={Sun} text="Opwek: 20 kWh" />
                <Row icon={HomeIcon} text="Direct verbruik: 6 kWh" />
                <Row icon={Send} text="Terug naar net: 14 kWh" tone="neg" />
                <Row icon={Coins} text="Lage terugleververgoeding" tone="neg" />
                <Row icon={Moon} text="'s Avonds stroom terugkopen" tone="neg" />
              </ul>

              <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-500/15 border border-rose-400/30 text-rose-200 text-xs font-semibold">
                <AlertTriangle size={12} />
                Meer afhankelijk van energieleveranciers
              </div>
            </div>
          </motion.div>

          {/* WITH */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, delay: 0.15 }}
            className="relative rounded-3xl border border-brand/30 bg-gradient-to-br from-brand/15 via-white/[0.04] to-transparent backdrop-blur-xl p-6 hover:-translate-y-1 transition-all duration-500 shadow-[0_20px_60px_rgba(255,47,125,0.16)]"
            data-testid="saldering-card-with"
          >
            <div className="absolute -inset-px rounded-3xl pointer-events-none bg-gradient-to-br from-brand/10 via-transparent to-emerald-500/5" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-9 h-9 rounded-xl gradient-pink text-white flex items-center justify-center flex-shrink-0">
                    <Check size={16} strokeWidth={2.5} />
                  </span>
                  <h3 className="font-heading text-lg font-black">Met thuisbatterij</h3>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-brand font-semibold leading-none">Besparing / jaar</div>
                  <div className="font-heading font-black text-2xl tabular-nums leading-none mt-1 gradient-pink-text" data-testid="sald-cost-with">
                    {hasResult ? fmtEuro(annualSavings) : "—"}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Eigen verbruik {Math.round(selfConsWith)}%</div>
                </div>
              </div>

              <ul className="space-y-2.5">
                <Row icon={Sun} text="Opwek: 20 kWh" />
                <Row icon={HomeIcon} text="Direct verbruik: 6 kWh" />
                <Row icon={Battery} text="Opslag in batterij: 14 kWh" tone="pos" />
                <Row icon={Moon} text="Eigen stroom 's avonds gebruiken" tone="pos" />
                <Row icon={ArrowDownCircle} text="Minder inkopen van het net" tone="pos" />
              </ul>

              <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-xs font-semibold">
                <ShieldCheck size={12} />
                Maximale benutting van eigen zonnestroom
              </div>
            </div>
          </motion.div>
        </div>

        {/* Personal savings highlight — driven by calculator */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.45 }}
          className="mt-6 rounded-2xl border border-brand/30 bg-gradient-to-r from-brand/15 via-brand/5 to-transparent p-5 flex items-center justify-between gap-4"
          data-testid="sald-savings-highlight"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-xl gradient-pink text-white flex items-center justify-center flex-shrink-0">
              <TrendingUp size={18} />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-brand font-bold">Jouw jaarlijkse besparing</div>
              <p className="text-slate-300 text-[13px] mt-0.5">
                {hasResult
                  ? <>Op basis van jouw invoer ({payload?.panels} panelen · {payload?.annual_usage_kwh?.toLocaleString("nl-NL")} kWh).</>
                  : "Vul de calculator hierboven in voor jouw persoonlijke besparing."}
              </p>
            </div>
          </div>
          <div className="font-heading font-black text-3xl md:text-4xl tabular-nums gradient-pink-text whitespace-nowrap" data-testid="sald-annual-savings">
            {hasResult ? fmtEuro(annualSavings) : "—"}
          </div>
        </motion.div>

        {/* Compact CTA block */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.45 }}
          className="mt-8 rounded-2xl relative overflow-hidden p-5 lg:p-6 border border-brand/30 bg-gradient-to-br from-brand/15 via-brand/5 to-transparent"
          data-testid="saldering-cta-block"
        >
          <div className="blob-pink absolute -top-16 -right-10 opacity-50 pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-xl md:text-2xl font-black tracking-tight text-balance">
                Benieuwd hoeveel jij kunt besparen na 2027?
              </h3>
              <p className="text-slate-300 mt-1.5 text-sm leading-relaxed">
                Bereken direct jouw persoonlijke situatie of vraag vrijblijvend gratis advies aan.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2.5 flex-shrink-0">
              <a
                href="#calculator"
                onClick={scrollToCalc}
                className="btn-pink !text-white !py-2.5 !px-5 !text-sm whitespace-nowrap"
                data-testid="saldering-cta-calc"
              >
                Bereken mijn situatie <ArrowRight size={16} />
              </a>
              <Link
                to="/contact"
                className="btn-ghost !bg-white/10 !text-white !border-white/20 hover:!bg-white/20 !py-2.5 !px-5 !text-sm whitespace-nowrap"
                data-testid="saldering-cta-quote"
              >
                Vraag gratis advies aan
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SalderingsSection;
